import axios, { AxiosInstance } from 'axios';

// ─── Types (shared with DEMS backend) ────────────────────────────────────────

export interface DemoUser {
  id: string;
  name: string;
  email: string;
  role: 'LawEnforcement' | 'Attorney' | 'PublicSubmitter' | 'CaseManager' | 'SystemAdmin' | 'Judge';
  department: string;
  badgeNumber?: string;
  barNumber?: string;
  isActive: boolean;
}

export interface Evidence {
  id: string;
  caseNumber: string;
  fileName: string;
  fileType: string;
  fileSize: number;
  sha256Hash: string;
  uploadedBy: string;
  uploadedAt: string;
  description: string;
  evidenceType: string;
  containerName: string;
  blobPath: string;
  integrityStatus: 'verified' | 'failed' | 'pending';
  legalHold: boolean;
  retentionExpiresAt: string;
  externalUrl?: string;
  thumbnailUrl?: string;
  title?: string;
  evidenceStatus?: 'Active' | 'PendingReview' | 'Retracted' | 'Rejected';
  AiExtracted?: boolean;
  AiIndexedAt?: string;
}

export interface CustodyChainEvent {
  id: string;
  evidenceId: string;
  eventType: 'upload' | 'download' | 'view' | 'transfer' | 'legal_hold' | 'integrity_check';
  performedBy: string;
  performedByName: string;
  performedAt: string;
  description: string;
  ipAddress: string;
  userAgent: string;
  previousHash?: string;
  newHash?: string;
  metadata?: Record<string, unknown>;
}

export interface Case {
  caseNumber: string;
  title: string;
  description: string;
  status: string;
  createdAt: string;
  assignedTo: string;
  jurisdiction: string;
  evidenceCount?: number;
  court?: string;
  type?: string;
  leadInvestigator?: string;
}

export interface AuthResponse {
  token: string;
  user: DemoUser;
  expiresAt: string;
}

// ─── API Base URL ────────────────────────────────────────────────────────────

function getApiBaseUrl(): string {
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }
  const hostname = window.location.hostname;
  if (hostname.includes('azurestaticapps.net') || hostname.includes('rtcc')) {
    return 'https://func-dems-api-dev.azurewebsites.net/api';
  }
  return '/api';
}

const API_BASE_URL = getApiBaseUrl();

// ─── Client ──────────────────────────────────────────────────────────────────

class ApiClient {
  private client: AxiosInstance;
  private token: string | null = null;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: 30000,
      headers: { 'Content-Type': 'application/json' },
    });

    this.client.interceptors.request.use((config) => {
      if (this.token) {
        config.headers.Authorization = `Bearer ${this.token}`;
      }
      return config;
    });

    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          this.token = null;
          localStorage.removeItem('rtcc_token');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );

    const savedToken = localStorage.getItem('rtcc_token');
    if (savedToken) {
      this.token = savedToken;
    }
  }

  setToken(token: string | null) {
    this.token = token;
    if (token) {
      localStorage.setItem('rtcc_token', token);
    } else {
      localStorage.removeItem('rtcc_token');
    }
  }

  getToken(): string | null {
    return this.token;
  }

  // ─── Auth ──────────────────────────────────────────────────────────────────

  async demoLogin(pin: string, userId?: string): Promise<AuthResponse> {
    const response = await this.client.post<{ success: boolean; data: { token: string; user: DemoUser; expiresIn: string } }>('/auth/demo-login', { pin, userId });
    const { token, user, expiresIn } = response.data.data;
    this.setToken(token);
    const hours = parseInt(expiresIn) || 8;
    const expiresAt = new Date(Date.now() + hours * 60 * 60 * 1000).toISOString();
    return { token, user, expiresAt };
  }

  async getCurrentUser(): Promise<DemoUser> {
    const response = await this.client.get<{ success: boolean; data: DemoUser }>('/auth/me');
    return response.data.data;
  }

  async getDemoUsers(): Promise<Omit<DemoUser, 'pin'>[]> {
    const response = await this.client.get<{ success: boolean; data: Omit<DemoUser, 'pin'>[] }>('/auth/demo-users');
    return response.data.data;
  }

  logout() {
    this.setToken(null);
  }

  // ─── Cases ─────────────────────────────────────────────────────────────────

  private mapBackendCase(c: Record<string, unknown>): Case {
    return {
      caseNumber: (c.CaseNumber || c.caseNumber) as string,
      title: (c.Title || c.title) as string,
      description: (c.Description || c.description || '') as string,
      status: (c.Status || c.status || 'open') as string,
      createdAt: (c.FilingDate || c.filingDate || c.createdAt || new Date().toISOString()) as string,
      assignedTo: (c.AssignedJudge || c.assignedJudge || c.assignedTo || '') as string,
      jurisdiction: (c.Court || c.court || c.jurisdiction || '') as string,
      evidenceCount: (c.EvidenceCount || c.evidenceCount || 0) as number,
      court: (c.Court || c.court) as string | undefined,
      type: (c.Type || c.type) as string | undefined,
      leadInvestigator: (c.LeadInvestigator || c.leadInvestigator) as string | undefined,
    };
  }

  async getCases(): Promise<Case[]> {
    const response = await this.client.get<{ success: boolean; data: Record<string, unknown>[]; total: number }>('/cases');
    return (response.data.data || []).map(this.mapBackendCase.bind(this));
  }

  async createCase(data: { title: string; type: 'Criminal' | 'Civil'; description?: string; court?: string; leadInvestigator?: string; caseNumber?: string }): Promise<Case> {
    const response = await this.client.post<{ success: boolean; data: Record<string, unknown> }>('/cases', data);
    return this.mapBackendCase(response.data.data);
  }

  async getCase(caseNumber: string): Promise<Case> {
    const response = await this.client.get<{ success: boolean; data: Record<string, unknown> }>(`/cases/${caseNumber}`);
    return this.mapBackendCase(response.data.data);
  }

  // ─── Evidence ──────────────────────────────────────────────────────────────

  private mapBackendEvidence(e: Record<string, unknown>): Evidence {
    let metadata: Record<string, string> = {};
    try {
      const raw = (e.Metadata || e.metadata) as string;
      if (raw) metadata = JSON.parse(raw);
    } catch { /* ignore */ }

    return {
      id: (e.Id || e.id) as string,
      caseNumber: (e.CaseNumber || e.caseNumber) as string,
      fileName: (e.FileName || e.fileName) as string,
      fileType: (e.MimeType || e.mimeType || e.fileType) as string,
      fileSize: (e.FileSize || e.fileSize) as number,
      sha256Hash: (e.Sha256Hash || e.sha256Hash) as string,
      uploadedBy: (e.UploadedBy || e.uploadedBy) as string,
      uploadedAt: (e.UploadedAt || e.uploadedAt) as string,
      description: (e.Description || e.description || '') as string,
      evidenceType: (e.Type || e.Category || e.evidenceType || 'Document') as string,
      containerName: (e.containerName || '') as string,
      blobPath: (e.BlobPath || e.blobPath || '') as string,
      integrityStatus: (e.integrityStatus || 'verified') as Evidence['integrityStatus'],
      legalHold: (e.legalHold || false) as boolean,
      retentionExpiresAt: (e.retentionExpiresAt || '') as string,
      externalUrl: (e.ExternalUrl || e.externalUrl || metadata.externalUrl) as string | undefined,
      thumbnailUrl: (e.ThumbnailUrl || e.thumbnailUrl || metadata.thumbnailUrl) as string | undefined,
      title: (e.Title || e.title || e.fileName) as string | undefined,
      evidenceStatus: (e.EvidenceStatus || e.evidenceStatus || 'Active') as Evidence['evidenceStatus'],
      AiExtracted: e.AiExtracted === true || (e.AiExtracted as unknown) === 1 ? true : e.AiExtracted === false || (e.AiExtracted as unknown) === 0 ? false : undefined,
      AiIndexedAt: (e.AiIndexedAt || e.aiIndexedAt || metadata.extractedAt) as string | undefined,
    };
  }

  async getCaseEvidence(caseNumber: string): Promise<Evidence[]> {
    const response = await this.client.get<{ success: boolean; data: Record<string, unknown>[]; total: number }>(`/cases/${caseNumber}/evidence`);
    return (response.data.data || []).map(this.mapBackendEvidence.bind(this));
  }

  async getEvidence(evidenceId: string): Promise<Evidence> {
    const response = await this.client.get<{ success: boolean; data: Record<string, unknown> }>(`/evidence/${evidenceId}`);
    return this.mapBackendEvidence(response.data.data);
  }

  async uploadEvidence(
    caseNumber: string,
    file: File,
    metadata: { description: string; evidenceType: string },
    onProgress?: (progress: number) => void
  ): Promise<Evidence> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('caseNumber', caseNumber);
    formData.append('title', file.name);
    formData.append('description', metadata.description);
    formData.append('type', metadata.evidenceType);

    const response = await this.client.post<{ success: boolean; data: { id: string; caseNumber: string; title: string; sha256Hash: string } }>(
      '/evidence/upload',
      formData,
      {
        headers: { 'Content-Type': undefined },
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            onProgress(Math.round((progressEvent.loaded * 100) / progressEvent.total));
          }
        },
      }
    );
    const data = response.data.data;
    return {
      id: data.id,
      caseNumber: data.caseNumber,
      fileName: file.name,
      fileType: file.type,
      fileSize: file.size,
      sha256Hash: data.sha256Hash,
      uploadedBy: '',
      uploadedAt: new Date().toISOString(),
      description: metadata.description,
      evidenceType: metadata.evidenceType,
      containerName: '',
      blobPath: '',
      integrityStatus: 'verified',
      legalHold: false,
      retentionExpiresAt: '',
    };
  }

  async getPreviewBlobUrl(evidenceId: string): Promise<string> {
    const token = this.getToken();
    const baseUrl = this.client.defaults.baseURL;
    const url = `${baseUrl}/evidence/${evidenceId}/stream`;
    const response = await fetch(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!response.ok) throw new Error('Preview unavailable');
    const blob = await response.blob();
    return URL.createObjectURL(blob);
  }

  async downloadEvidence(evidenceId: string): Promise<{ url: string; filename: string }> {
    const evidence = await this.getEvidence(evidenceId);
    if (evidence.externalUrl) {
      return { url: evidence.externalUrl, filename: evidence.fileName };
    }
    const token = this.getToken();
    const baseUrl = this.client.defaults.baseURL;
    return {
      url: `${baseUrl}/evidence/${evidenceId}/download${token ? `?token=${token}` : ''}`,
      filename: evidence.fileName,
    };
  }

  // ─── Custody Chain ─────────────────────────────────────────────────────────

  private mapBackendCustodyEvent(e: Record<string, unknown>): CustodyChainEvent {
    const eventTypeMap: Record<string, string> = {
      'Upload': 'upload', 'Uploaded': 'upload', 'Download': 'download',
      'Access': 'view', 'View': 'view', 'Share': 'transfer', 'Transfer': 'transfer',
      'LegalHold': 'legal_hold', 'IntegrityCheck': 'integrity_check',
    };
    const backendType = (e.EventType || e.eventType || 'view') as string;
    const mappedType = eventTypeMap[backendType] || backendType.toLowerCase();

    return {
      id: (e.Id || e.id) as string,
      evidenceId: (e.EvidenceId || e.evidenceId) as string,
      eventType: mappedType as CustodyChainEvent['eventType'],
      performedBy: (e.Actor || e.actor || e.performedBy) as string,
      performedByName: (e.ActorName || e.actorName || e.performedByName) as string,
      performedAt: (e.Timestamp || e.timestamp || e.performedAt) as string,
      description: (e.Description || e.description) as string,
      ipAddress: (e.IpAddress || e.ipAddress || '') as string,
      userAgent: (e.userAgent || '') as string,
      previousHash: e.previousHash as string | undefined,
      newHash: e.newHash as string | undefined,
      metadata: e.Metadata ? (typeof e.Metadata === 'string' ? JSON.parse(e.Metadata as string) : e.Metadata) as Record<string, unknown> : e.metadata as Record<string, unknown> | undefined,
    };
  }

  async getEvidenceCustodyChain(evidenceId: string): Promise<CustodyChainEvent[]> {
    const response = await this.client.get<{ success: boolean; data: { events: Record<string, unknown>[] } }>(`/custody/${evidenceId}`);
    return (response.data.data?.events || []).map(this.mapBackendCustodyEvent.bind(this));
  }

  async logViewEvent(evidenceId: string): Promise<void> {
    await this.client.post(`/custody/${evidenceId}/log-view`);
  }

  async logDownloadEvent(evidenceId: string): Promise<void> {
    await this.client.post(`/custody/${evidenceId}/log-download`);
  }

  // ─── AI Agents ─────────────────────────────────────────────────────────────

  async aegisAsk(evidenceId: string, question: string, caseNumber: string): Promise<{ answer: string; source?: unknown }> {
    const response = await this.client.post<{ success: boolean; answer: string; source?: unknown }>('/aegis/ask', {
      evidenceId,
      question,
      caseNumber,
    });
    if (!response.data.success) throw new Error('Agent Aegis request failed');
    return { answer: response.data.answer, source: response.data.source };
  }

  async aegisInvestigate(question: string, caseNumber: string, filters?: { category?: string; evidenceType?: string }): Promise<{ answer: string; evidenceCount: number; sources: Array<{ evidenceId: string; title: string; fileName: string; category: string; evidenceType: string; summary?: string }> }> {
    const response = await this.client.post<{ success: boolean; answer: string; evidenceCount: number; sources: Array<{ evidenceId: string; title: string; fileName: string; category: string; evidenceType: string; summary?: string }> }>('/aegis/investigate', {
      question,
      caseNumber,
      filters,
    });
    if (!response.data.success) throw new Error('Agent Aegis investigate failed');
    return { answer: response.data.answer, evidenceCount: response.data.evidenceCount, sources: response.data.sources };
  }

  async aegisSearch(query: string, caseNumber: string, options?: { category?: string; type?: string; top?: number }): Promise<{ results: unknown[]; answer?: string }> {
    const params = new URLSearchParams({ q: query, caseNumber });
    if (options?.category) params.append('category', options.category);
    if (options?.type) params.append('type', options.type);
    if (options?.top) params.append('top', options.top.toString());
    const response = await this.client.get<{ success: boolean; results: unknown[]; answer?: string }>(`/aegis/search?${params}`);
    return { results: response.data.results || [], answer: response.data.answer };
  }

  async getVideoInsights(evidenceId: string): Promise<VideoInsights | null> {
    try {
      const response = await this.client.get<{ success: boolean; data: VideoInsights }>(`/evidence/${evidenceId}/insights`);
      return response.data.data;
    } catch {
      return null;
    }
  }

  // ─── Health ────────────────────────────────────────────────────────────────

  async healthCheck(): Promise<{ status: string; timestamp: string }> {
    const response = await this.client.get('/health');
    return response.data;
  }
}

// ─── Video Insights Type ─────────────────────────────────────────────────────

export interface VideoInsights {
  videoIndexerId?: string;
  duration?: string;
  durationSeconds?: number;
  scenes?: number;
  shots?: number;
  transcript?: string;
  faces?: Array<{ id: string; name?: string; confidence?: number; instances?: Array<{ start: string; end: string }> }>;
  observedPeople?: Array<{ id: number; clothing?: Array<{ type: string; properties: Record<string, string> }>; instances?: Array<{ adjustedStart: string; adjustedEnd: string }> }>;
  labels?: string[];
  keywords?: string[];
  ocr?: Array<{ text: string; confidence?: number }>;
  plates?: Array<{ text: string; confidence?: number; timestamp?: string }>;
  // Vision enrichment
  detectedObjects?: Array<{ id: number; type: string; displayName: string; wikiDataId?: string; thumbnailId?: string; instances?: Array<{ confidence: number; adjustedStart: string; adjustedEnd: string }> }>;
  visionEnrichment?: {
    sceneDescription: string;
    people: Array<{ description: string; clothing: string; action: string; position: string }>;
    vehicles: Array<{ type: string; color: string; details: string; licensePlate?: string }>;
    objects: Array<{ name: string; description: string; significance: string }>;
    weapons: string[];
    licensePlates: string[];
    text: Array<{ content: string; type: string }>;
    location: { type: string; details: string };
    lighting: string;
    actions: string[];
  };
}

const apiClient = new ApiClient();
export default apiClient;
